import { httpClient } from "@/shared/api/http-client";
import type {
  BulkFoundingResult,
  FoundingAccountStatus,
  FoundingEnrollmentDetail,
  FoundingProgram,
  FoundingRates,
  FoundingReportFilters,
  FoundingReportResult,
  OwnerPropertiesResult,
} from "./types";

/**
 * Hand-written fetches for the `wanderfit-api` Founding Partners admin surface
 * (`/api/admin/founding/*`, ADR-0010) — see CLAUDE.md → TD-07.
 */

export function buildReportQuery(filters: FoundingReportFilters): string {
  const params = new URLSearchParams();
  const country = filters.country.trim();
  const tier = filters.tier.trim();
  if (country) params.set("country", country);
  if (tier) params.set("tier", tier);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function fetchFoundingReport(filters: FoundingReportFilters): Promise<FoundingReportResult> {
  return httpClient<FoundingReportResult>(`/api/admin/founding/report${buildReportQuery(filters)}`);
}

/**
 * A bulk assign body (ADR-0010 §9). The server accepts exactly the shape the
 * chosen program/structure needs:
 *  - FOUNDING_HOST → `introPercent` + `ongoingPercent` (no `flatPercent`).
 *  - PORTFOLIO_CUSTOM two-phase → `introPercent` + `ongoingPercent`.
 *  - PORTFOLIO_CUSTOM flat → `flatPercent` only.
 * Never send `flatPercent` together with `intro`/`ongoing`.
 */
export interface AssignFoundingBulkBody {
  propertyIds: string[];
  program: FoundingProgram;
  introPercent?: number;
  ongoingPercent?: number;
  flatPercent?: number;
}

export function assignFoundingBulk(body: AssignFoundingBulkBody): Promise<BulkFoundingResult> {
  return httpClient<BulkFoundingResult>("/api/admin/founding/assign-bulk", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * The configured founding-discount defaults (`GET /admin/founding/rates`,
 * ADR-0010 §9). Used to pre-fill the assign dialog's intro/ongoing inputs.
 * Roles ADMIN + FINANCE.
 */
export function fetchFoundingRates(): Promise<FoundingRates> {
  return httpClient<FoundingRates>("/api/admin/founding/rates");
}

export function unassignFoundingBulk(propertyIds: string[]): Promise<BulkFoundingResult> {
  return httpClient<BulkFoundingResult>("/api/admin/founding/unassign-bulk", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ propertyIds }),
  });
}

/**
 * Account-level Founding Partner status for the badge + user/venue detail
 * (ADR-0010 §9). 404 when the user is missing; roles ADMIN + FINANCE.
 */
export function fetchFoundingAccount(userId: string): Promise<FoundingAccountStatus> {
  return httpClient<FoundingAccountStatus>(
    `/api/admin/founding/accounts/${encodeURIComponent(userId)}`,
  );
}

export interface SetFoundingPartnerBody {
  userId: string;
  enabled: boolean;
}

/**
 * Convert / un-convert an account to a Founding Partner (ADR-0010 §9, ADMIN
 * only). Enabling a host turns on grandfathering at the ongoing rate — there
 * is no chosen percent. Returns the fresh account status.
 */
export function setFoundingPartner(body: SetFoundingPartnerBody): Promise<FoundingAccountStatus> {
  const { userId, ...rest } = body;
  return httpClient<FoundingAccountStatus>(
    `/api/admin/founding/accounts/${encodeURIComponent(userId)}/partner`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(rest),
    },
  );
}

export interface OwnerPropertiesFilters {
  readonly query?: string;
  readonly status?: string;
  readonly page?: number;
  readonly limit?: number;
}

/**
 * Build the query string for a host's property list. Mirrors
 * `buildReportQuery`: empty / default / sentinel values are omitted so the
 * URL stays minimal and the React Query cache key matches the network call.
 */
export function buildOwnerPropertiesQuery(filters: OwnerPropertiesFilters): string {
  const params = new URLSearchParams();
  const query = filters.query?.trim();
  const status = filters.status?.trim();
  if (query) params.set("query", query);
  if (status && status !== "all") params.set("status", status);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.limit && filters.limit > 0) {
    params.set("limit", String(filters.limit));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function fetchOwnerFoundingProperties(
  userId: string,
  filters: OwnerPropertiesFilters,
): Promise<OwnerPropertiesResult> {
  return httpClient<OwnerPropertiesResult>(
    `/api/admin/founding/accounts/${encodeURIComponent(userId)}/properties${buildOwnerPropertiesQuery(filters)}`,
  );
}

/**
 * The full founding enrollment for a single property (ADR-0010, ADMIN +
 * FINANCE). Returns `null` (200) when the property has no enrollment.
 */
export function fetchFoundingEnrollment(
  propertyId: string,
): Promise<FoundingEnrollmentDetail | null> {
  return httpClient<FoundingEnrollmentDetail | null>(
    `/api/admin/founding/enrollments/${encodeURIComponent(propertyId)}`,
  );
}

export interface EditFoundingRatesBody {
  propertyId: string;
  introPercent?: number;
  ongoingPercent?: number;
  flatPercent?: number;
}

/**
 * Override a property's founding rates (ADR-0010, ADMIN only). Only the
 * provided percents are sent; the change takes effect at the NEXT RENEWAL.
 * Returns the updated enrollment.
 */
export function editFoundingRates(body: EditFoundingRatesBody): Promise<FoundingEnrollmentDetail> {
  const { propertyId, ...rest } = body;
  return httpClient<FoundingEnrollmentDetail>(
    `/api/admin/founding/enrollments/${encodeURIComponent(propertyId)}/rates`,
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(rest),
    },
  );
}
